import * as React from 'react';
import { Button } from 'Src/components/Button/Button';
import { ClubProgrammeContentToolbarHeader } from './ClubProgrammeContentToolbarHeader';
import { ClubProgrammeFilterControl } from './ClubProgrammeFilterControl';
import { getClubProgrammePopoverStyle } from 'Src/helpers/clubProgramme/clubProgrammeUi';
import { WEEK_DAYS_ORDER, getProgrammeClubDayShortLabel } from '../helpers/clubProgrammeClubsContent';
import { formatAgesCompact } from 'Src/helpers/ageGroups';
import { AddClubsColumnDefinition, AddClubsColumnKey } from './ClubProgrammeAddClubsTable';

const ColumnsIcon = () => (
  <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <rect x="1.5" y="2" width="13" height="12" rx="1.5" stroke="currentColor" strokeWidth="1.4" />
    <path d="M6.5 2v12M10.5 2v12" stroke="currentColor" strokeWidth="1.4" />
  </svg>
);

const CheckIcon = () => (
  <span className="sit-cols__cb">
    <svg viewBox="0 0 16 16" aria-hidden="true">
      <path d="M3 8.5l3.2 3.2L13 4.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  </span>
);

const LockIcon = () => (
  <svg className="sit-cols__lock" width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="5" y="11" width="14" height="9" rx="2" stroke="currentColor" strokeWidth="1.8" />
    <path d="M8 11V8a4 4 0 0 1 8 0v3" stroke="currentColor" strokeWidth="1.8" />
  </svg>
);

type AddClubsColumnsMenuProps = {
  columns: AddClubsColumnDefinition[];
  hiddenColumns: AddClubsColumnKey[];
  onToggleColumn: (key: AddClubsColumnKey) => void;
  onResetColumns: () => void;
};

const AddClubsColumnsMenu: React.FC<AddClubsColumnsMenuProps> = ({
  columns,
  hiddenColumns,
  onToggleColumn,
  onResetColumns
}) => {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef<HTMLSpanElement | null>(null);
  const hiddenSet = React.useMemo(() => new Set(hiddenColumns || []), [hiddenColumns.join('|')]);
  const shownCount = columns.filter(column => column.locked || !hiddenSet.has(column.key)).length;

  React.useEffect(() => {
    if (!open) return;

    const onMouseDown = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false);
    };

    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [open]);

  return (
    <span className="sit-cols cpAddClubsColumnsMenu" ref={ref}>
      <button
        type="button"
        className={['sit-cols__btn', open ? 'sit-cols__btn--open' : ''].filter(Boolean).join(' ')}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen(value => !value)}
      >
        <ColumnsIcon />
        Columns
        <span className="sit-cols__badge">{shownCount}</span>
      </button>

      {open ? (
        <div className="sit-cols__pop sit-cols__pop--right" role="menu">
          <div className="sit-cols__hd">
            <span className="sit-cols__title">Show / hide columns</span>
            <button type="button" className="sit-cols__reset" onClick={onResetColumns}>
              Reset
            </button>
          </div>

          <div className="sit-cols__list sit-scroll">
            {columns.map(column => {
              const isOn = column.locked || !hiddenSet.has(column.key);

              if (column.locked) {
                return (
                  <div key={column.key} className="sit-cols__item sit-cols__item--locked" data-on="true">
                    <CheckIcon />
                    {column.header}
                    <LockIcon />
                  </div>
                );
              }

              return (
                <button
                  key={column.key}
                  type="button"
                  role="menuitemcheckbox"
                  aria-checked={isOn}
                  className="sit-cols__item"
                  data-on={isOn}
                  onClick={() => onToggleColumn(column.key)}
                >
                  <CheckIcon />
                  {column.header}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </span>
  );
};
type Props = {
  isAddFiltersOpen: boolean;
  setIsAddFiltersOpen: React.Dispatch<React.SetStateAction<boolean>>;

  programmeClubsLength: number;
  isProgrammeClubsLoading: boolean;

  isAddWorking: boolean;
  isSportsLoading: boolean;

  addSearch: string;
  setAddSearch: React.Dispatch<React.SetStateAction<string>>;
  onApplySearch: () => void;

  addMode: 'ELIGIBLE_ONLY' | 'SHOW_ALL';
  setAddMode: React.Dispatch<React.SetStateAction<'ELIGIBLE_ONLY' | 'SHOW_ALL'>>;

  addSportIds: string[];
  setAddSportIds: React.Dispatch<React.SetStateAction<string[]>>;
  addSportDropdownOpen: boolean;
  setAddSportDropdownOpen: React.Dispatch<React.SetStateAction<boolean>>;
  addSportSearch: string;
  setAddSportSearch: React.Dispatch<React.SetStateAction<string>>;
  addActivitiesWrapRef: React.RefObject<HTMLDivElement | null>;
  sportOptions: Array<{ id: string; name: string }>;

  addDays: string[];
  setAddDays: React.Dispatch<React.SetStateAction<string[]>>;
  addDayDropdownOpen: boolean;
  setAddDayDropdownOpen: React.Dispatch<React.SetStateAction<boolean>>;
  addDayWrapRef: React.RefObject<HTMLDivElement | null>;
  addDaysLabel: string;

  addGenders: string[];
  setAddGenders: React.Dispatch<React.SetStateAction<string[]>>;
  addGenderDropdownOpen: boolean;
  setAddGenderDropdownOpen: React.Dispatch<React.SetStateAction<boolean>>;
  addGenderWrapRef: React.RefObject<HTMLDivElement | null>;
  addGenderOptions: Array<{ id: string; label: string }>;

  addAgeValues: number[];
  setAddAgeValues: React.Dispatch<React.SetStateAction<number[]>>;
  addAgeDropdownOpen: boolean;
  setAddAgeDropdownOpen: React.Dispatch<React.SetStateAction<boolean>>;
  addAgeSearch: string;
  setAddAgeSearch: React.Dispatch<React.SetStateAction<string>>;
  addAgeWrapRef: React.RefObject<HTMLDivElement | null>;
  addAgesLabel: string;
  ageOptions: Array<{ age: number; label: string }>;
  naming: any;

  addStartFrom: string;
  setAddStartFrom: React.Dispatch<React.SetStateAction<string>>;
  addStartTo: string;
  setAddStartTo: React.Dispatch<React.SetStateAction<string>>;
  onResetFilters: () => void;

  columns: AddClubsColumnDefinition[];
  hiddenColumns: AddClubsColumnKey[];
  onToggleColumn: (key: AddClubsColumnKey) => void;
  onResetColumns: () => void;
};

export const ClubProgrammeAddClubsFilters: React.FC<Props> = ({
  isAddFiltersOpen,
  setIsAddFiltersOpen,
  programmeClubsLength,
  isProgrammeClubsLoading,
  isAddWorking,
  isSportsLoading,
  addSearch,
  setAddSearch,
  onApplySearch,
  addMode,
  setAddMode,
  addSportIds,
  setAddSportIds,
  addSportDropdownOpen,
  setAddSportDropdownOpen,
  addSportSearch,
  setAddSportSearch,
  addActivitiesWrapRef,
  sportOptions,
  addDays,
  setAddDays,
  addDayDropdownOpen,
  setAddDayDropdownOpen,
  addDayWrapRef,
  addDaysLabel,
  addGenders,
  setAddGenders,
  addGenderDropdownOpen,
  setAddGenderDropdownOpen,
  addGenderWrapRef,
  addGenderOptions,
  addAgeValues,
  setAddAgeValues,
  addAgeDropdownOpen,
  setAddAgeDropdownOpen,
  addAgeSearch,
  setAddAgeSearch,
  addAgeWrapRef,
  addAgesLabel,
  ageOptions,
  naming,
  addStartFrom,
  setAddStartFrom,
  addStartTo,
  setAddStartTo,
  onResetFilters,
  columns,
  hiddenColumns,
  onToggleColumn,
  onResetColumns
}) => {
  return (
    <div className="cpToolbar">
      <ClubProgrammeContentToolbarHeader
        isFiltersOpen={isAddFiltersOpen}
        onToggleFilters={() => setIsAddFiltersOpen(v => !v)}
        actions={
          <AddClubsColumnsMenu
            columns={columns}
            hiddenColumns={hiddenColumns}
            onToggleColumn={onToggleColumn}
            onResetColumns={onResetColumns}
          />
        }
        rightText={
          <>
            Added already: <b>{programmeClubsLength}</b>
            {isProgrammeClubsLoading ? ' (refreshing linked clubs...)' : ''}
          </>
        }
      />

      {isAddFiltersOpen ? (
        <div className="mt-2">
          <div
            className="d-flex flex-wrap align-items-start justify-content-between"
            style={{ gap: 12, paddingBottom: 8 }}
          >
            <div className="d-flex flex-wrap align-items-end" style={{ gap: 8 }}>
              <ClubProgrammeFilterControl width={220}>
                <div className="cpFilterLabel">Search</div>
                <input
                  className="form-control cpInputSm"
                  placeholder="Search…"
                  value={addSearch}
                  onChange={e => setAddSearch(e.target.value)}
                  onKeyDown={event => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      onApplySearch();
                    }
                  }}
                />
              </ClubProgrammeFilterControl>

              <ClubProgrammeFilterControl width={260}>
                <div className="cpFilterLabel">Mode</div>
                <select
                  className="form-control cpInputSm"
                  value={addMode}
                  onChange={e => setAddMode(e.target.value as 'ELIGIBLE_ONLY' | 'SHOW_ALL')}
                  disabled={isAddWorking}
                  title="Eligible only is recommended. Show all is advanced."
                >
                  <option value="ELIGIBLE_ONLY">Eligible only (recommended)</option>
                  <option value="SHOW_ALL">Show all clubs (advanced)</option>
                </select>
              </ClubProgrammeFilterControl>
            </div>

            <div
              className="d-flex flex-wrap align-items-end justify-content-between"
              style={{ gap: 12, width: '100%' }}
            >
              <div className="d-flex flex-wrap align-items-end" style={{ gap: 8 }}>
                <ClubProgrammeFilterControl width={220} relative ref={addActivitiesWrapRef as any}>
                  <div className="cpFilterLabel">Activities</div>
                  <div
                    className="form-control cpControlSm"
                    onClick={() => {
                      if (isAddWorking || isSportsLoading) return;
                      setAddSportDropdownOpen(!addSportDropdownOpen);
                    }}
                    title={
                      isSportsLoading
                        ? 'Loading…'
                        : addSportIds.length === 0
                        ? 'All activities'
                        : `${addSportIds.length} selected`
                    }
                  >
                    <span className="cpEllipsis">
                      {isSportsLoading
                        ? 'Loading…'
                        : addSportIds.length === 0
                        ? 'All activities'
                        : `${addSportIds.length} selected`}
                    </span>
                    <span style={{ marginLeft: 8, flex: '0 0 auto' }}>▾</span>
                  </div>

                  {addSportDropdownOpen && !isSportsLoading ? (
                    <div style={getClubProgrammePopoverStyle(360)} onClick={e => e.stopPropagation()}>
                      <input
                        className="form-control cpInputSm"
                        placeholder="Search activity…"
                        value={addSportSearch}
                        onChange={e => setAddSportSearch(e.target.value)}
                        disabled={isAddWorking}
                      />

                      <div className="d-flex align-items-center justify-content-between mt-2">
                        <button
                          type="button"
                          className="btn btn-link p-0"
                          disabled={isAddWorking}
                          onClick={() => {
                            const q = (addSportSearch || '').trim().toLowerCase();
                            const filtered = (sportOptions || []).filter(s =>
                              !q
                                ? true
                                : String(s.name || '')
                                    .toLowerCase()
                                    .includes(q)
                            );
                            setAddSportIds(filtered.map(s => String(s.id)));
                          }}
                        >
                          Select filtered
                        </button>

                        <button
                          type="button"
                          className="btn btn-link p-0"
                          disabled={isAddWorking || addSportIds.length === 0}
                          onClick={() => setAddSportIds([])}
                        >
                          Clear
                        </button>

                        <button
                          type="button"
                          className="btn btn-sm btn-secondary"
                          onClick={() => setAddSportDropdownOpen(false)}
                          disabled={isAddWorking}
                        >
                          Done
                        </button>
                      </div>

                      <div style={{ maxHeight: 240, overflowY: 'auto', marginTop: 8, paddingRight: 4 }}>
                        {(() => {
                          const q = (addSportSearch || '').trim().toLowerCase();
                          const filtered = (sportOptions || []).filter(s =>
                            !q
                              ? true
                              : String(s.name || '')
                                  .toLowerCase()
                                  .includes(q)
                          );
                          if (filtered.length === 0) return <div className="text-muted">No activities found.</div>;

                          return filtered.map(s => {
                            const id = String(s.id);
                            const checked = addSportIds.some(x => String(x) === id);

                            return (
                              <label
                                key={id}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 8,
                                  margin: 0,
                                  padding: '6px 4px',
                                  cursor: isAddWorking ? 'not-allowed' : 'pointer'
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  disabled={isAddWorking}
                                  onChange={() => {
                                    setAddSportIds(prev => {
                                      const exists = prev.some(x => String(x) === id);
                                      return exists ? prev.filter(x => String(x) !== id) : [...prev, id];
                                    });
                                  }}
                                />
                                <span className="cpEllipsis" title={String(s.name || '')}>
                                  {String(s.name || '')}
                                </span>
                              </label>
                            );
                          });
                        })()}
                      </div>
                    </div>
                  ) : null}
                </ClubProgrammeFilterControl>

                <ClubProgrammeFilterControl width={160} relative ref={addDayWrapRef as any}>
                  <div className="cpFilterLabel">Days</div>
                  <div
                    className="form-control cpControlSm"
                    onClick={() => {
                      if (isAddWorking) return;
                      setAddDayDropdownOpen(!addDayDropdownOpen);
                    }}
                    title={addDaysLabel}
                  >
                    <span className="cpEllipsis">{addDaysLabel}</span>
                    <span style={{ marginLeft: 8, flex: '0 0 auto' }}>▾</span>
                  </div>

                  {addDayDropdownOpen ? (
                    <div style={getClubProgrammePopoverStyle(280)} onClick={e => e.stopPropagation()}>
                      <div className="d-flex align-items-center justify-content-between">
                        <button
                          type="button"
                          className="btn btn-link p-0"
                          disabled={isAddWorking}
                          onClick={() => setAddDays(WEEK_DAYS_ORDER)}
                        >
                          Select all
                        </button>
                        <button
                          type="button"
                          className="btn btn-link p-0"
                          disabled={isAddWorking || addDays.length === 0}
                          onClick={() => setAddDays([])}
                        >
                          Clear
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-secondary"
                          disabled={isAddWorking}
                          onClick={() => setAddDayDropdownOpen(false)}
                        >
                          Done
                        </button>
                      </div>

                      <div style={{ marginTop: 8 }}>
                        {WEEK_DAYS_ORDER.map(d => {
                          const checked = addDays.includes(d);
                          return (
                            <label
                              key={d}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                margin: 0,
                                padding: '6px 4px',
                                cursor: isAddWorking ? 'not-allowed' : 'pointer'
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                disabled={isAddWorking}
                                onChange={() =>
                                  setAddDays(prev => (prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d]))
                                }
                              />
                              <span>{getProgrammeClubDayShortLabel(d)}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  ) : null}
                </ClubProgrammeFilterControl>

                <ClubProgrammeFilterControl width={160} relative ref={addGenderWrapRef as any}>
                  <div className="cpFilterLabel">Gender</div>
                  <div
                    className="form-control cpControlSm"
                    onClick={() => {
                      if (isAddWorking) return;
                      setAddGenderDropdownOpen(!addGenderDropdownOpen);
                    }}
                    title={addGenders.length === 0 ? 'All genders' : `${addGenders.length} selected`}
                  >
                    <span className="cpEllipsis">
                      {addGenders.length === 0 ? 'All genders' : `${addGenders.length} selected`}
                    </span>
                    <span style={{ marginLeft: 8, flex: '0 0 auto' }}>▾</span>
                  </div>

                  {addGenderDropdownOpen ? (
                    <div style={getClubProgrammePopoverStyle(240)} onClick={e => e.stopPropagation()}>
                      <div className="d-flex align-items-center justify-content-between">
                        <button
                          type="button"
                          className="btn btn-link p-0"
                          disabled={isAddWorking || addGenders.length === 0}
                          onClick={() => setAddGenders([])}
                        >
                          Clear
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-secondary"
                          disabled={isAddWorking}
                          onClick={() => setAddGenderDropdownOpen(false)}
                        >
                          Done
                        </button>
                      </div>

                      <div style={{ marginTop: 8 }}>
                        {(addGenderOptions || []).map(x => {
                          const checked = addGenders.includes(x.id);
                          return (
                            <label
                              key={x.id}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                margin: 0,
                                padding: '6px 4px',
                                cursor: isAddWorking ? 'not-allowed' : 'pointer'
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                disabled={isAddWorking}
                                onChange={() =>
                                  setAddGenders(prev =>
                                    prev.includes(x.id) ? prev.filter(v => v !== x.id) : [...prev, x.id]
                                  )
                                }
                              />
                              <span>{x.label}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  ) : null}
                </ClubProgrammeFilterControl>

                <ClubProgrammeFilterControl width={220} relative ref={addAgeWrapRef as any}>
                  <div className="cpFilterLabel">Ages</div>
                  <div
                    className="form-control cpControlSm"
                    onClick={() => {
                      if (isAddWorking) return;
                      setAddAgeDropdownOpen(!addAgeDropdownOpen);
                    }}
                    title={addAgeValues.length ? formatAgesCompact(addAgeValues, naming) : 'All ages'}
                  >
                    <span className="cpEllipsis">{addAgesLabel}</span>
                    <span style={{ marginLeft: 8, flex: '0 0 auto' }}>▾</span>
                  </div>

                  {addAgeDropdownOpen ? (
                    <div style={getClubProgrammePopoverStyle(360)} onClick={e => e.stopPropagation()}>
                      <input
                        className="form-control cpInputSm"
                        placeholder="Search age…"
                        value={addAgeSearch}
                        onChange={e => setAddAgeSearch(e.target.value)}
                        disabled={isAddWorking}
                      />

                      <div className="d-flex align-items-center justify-content-between mt-2">
                        <button
                          type="button"
                          className="btn btn-link p-0"
                          disabled={isAddWorking}
                          onClick={() => {
                            const q = (addAgeSearch || '').trim().toLowerCase();
                            const filtered = (ageOptions || []).filter(o =>
                              !q
                                ? true
                                : String(o.label || '')
                                    .toLowerCase()
                                    .includes(q)
                            );
                            setAddAgeValues(filtered.map(o => o.age));
                          }}
                        >
                          Select filtered
                        </button>

                        <button
                          type="button"
                          className="btn btn-link p-0"
                          disabled={isAddWorking || addAgeValues.length === 0}
                          onClick={() => setAddAgeValues([])}
                        >
                          Clear
                        </button>

                        <button
                          type="button"
                          className="btn btn-sm btn-secondary"
                          disabled={isAddWorking}
                          onClick={() => setAddAgeDropdownOpen(false)}
                        >
                          Done
                        </button>
                      </div>

                      <div style={{ maxHeight: 240, overflowY: 'auto', marginTop: 8, paddingRight: 4 }}>
                        {(() => {
                          const q = (addAgeSearch || '').trim().toLowerCase();
                          const filtered = (ageOptions || []).filter(o =>
                            !q
                              ? true
                              : String(o.label || '')
                                  .toLowerCase()
                                  .includes(q)
                          );
                          if (filtered.length === 0) return <div className="text-muted">No ages found.</div>;

                          return filtered.map(o => {
                            const checked = addAgeValues.includes(o.age);
                            return (
                              <label
                                key={String(o.age)}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 8,
                                  margin: 0,
                                  padding: '6px 4px',
                                  cursor: isAddWorking ? 'not-allowed' : 'pointer'
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  disabled={isAddWorking}
                                  onChange={() =>
                                    setAddAgeValues(prev =>
                                      prev.includes(o.age) ? prev.filter(v => v !== o.age) : [...prev, o.age]
                                    )
                                  }
                                />
                                <span title={String(o.label || '')} className="cpEllipsis">
                                  {o.label}
                                </span>
                              </label>
                            );
                          });
                        })()}
                      </div>
                    </div>
                  ) : null}
                </ClubProgrammeFilterControl>

                <ClubProgrammeFilterControl width={160}>
                  <div className="cpFilterLabel">Start from</div>
                  <input
                    type="date"
                    className="form-control cpInputSm"
                    value={addStartFrom}
                    onChange={e => setAddStartFrom(e.target.value)}
                    disabled={isAddWorking}
                    title="Start from"
                  />
                </ClubProgrammeFilterControl>

                <ClubProgrammeFilterControl width={160}>
                  <div className="cpFilterLabel">Start to</div>
                  <input
                    type="date"
                    className="form-control cpInputSm"
                    value={addStartTo}
                    onChange={e => setAddStartTo(e.target.value)}
                    disabled={isAddWorking}
                    title="Start to"
                  />
                </ClubProgrammeFilterControl>
              </div>

              <div className="d-flex flex-wrap align-items-center" style={{ gap: 12, minHeight: 56 }}>
                <Button
                  text="Reset filters"
                  onClick={onResetFilters}
                  disabled={isAddWorking}
                  customClass="btn-outline-secondary"
                />
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};
